import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { commissionSummary, registrationAlerts, reportSummary } from '../lib/api';
import { dateOnly, friendlyError, lostReasonLabel, minutes, roleLabel, rupees, sourceLabel, copyText } from '../lib/format';
import type { CommissionSummary, RegistrationAlert, ReportSummary } from '../lib/types';
import { Button, ErrorText, Notice, PageHeader } from '../components/ui';

type PeriodKey = 'today' | 'week' | 'month' | '30d';
const PERIODS: Record<PeriodKey, string> = { today: 'Today', week: 'This week', month: 'This month', '30d': 'Last 30 days' };

function istMidnight(d = new Date()) {
  const ist = new Date(d.getTime() + 5.5 * 3600e3);
  return new Date(Date.UTC(ist.getUTCFullYear(), ist.getUTCMonth(), ist.getUTCDate()) - 5.5 * 3600e3);
}
function bounds(k: PeriodKey): [Date, Date] {
  const now = new Date();
  const today = istMidnight(now);
  const end = new Date(now.getTime() + 60e3);
  if (k === 'today') return [today, end];
  if (k === 'week') { const dow = (new Date(today.getTime() + 5.5 * 3600e3).getUTCDay() + 6) % 7; return [new Date(today.getTime() - dow * 86400e3), end]; }
  if (k === 'month') { const ist = new Date(today.getTime() + 5.5 * 3600e3); return [new Date(Date.UTC(ist.getUTCFullYear(), ist.getUTCMonth(), 1) - 5.5 * 3600e3), end]; }
  return [new Date(today.getTime() - 29 * 86400e3), end];
}

export function Dashboard() {
  const { firm } = useAuth();
  const [period, setPeriod] = useState<PeriodKey>('month');
  const [data, setData] = useState<ReportSummary | null>(null);
  const [today, setToday] = useState<ReportSummary | null>(null);
  const [error, setError] = useState('');
  const [regs, setRegs] = useState<RegistrationAlert[]>([]);
  const [money, setMoney] = useState<CommissionSummary | null>(null);

  const load = useCallback(async () => {
    try {
      const [a, b] = bounds(period);
      const [t0, t1] = bounds('today');
      const [r, t] = await Promise.all([reportSummary(a.toISOString(), b.toISOString()), reportSummary(t0.toISOString(), t1.toISOString())]);
      setData(r); setToday(t); setError('');
      registrationAlerts().then((a) => setRegs(a.filter((x) => !x.is_test))).catch(() => setRegs([]));
      commissionSummary(false).then(setMoney).catch(() => setMoney(null));
    } catch (e) { setError(friendlyError(e)); }
  }, [period]);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const n = data?.now;
  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Real leads only. Simulator test data is left out of every number." />
      <ErrorText>{error}</ErrorText>

      {n && (
        <section className="mb-6">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted">Needs you now</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Tile n={n.response_late} label="Responses late" tone="bad" to="/leads" />
            <Tile n={n.held_reviews} label="AI replies to review" tone="warn" to="/review" />
            <Tile n={n.needs_you} label="Leads need a person" tone="bad" to="/leads" />
            <Tile n={n.unassigned} label="Unassigned leads" tone="warn" to="/leads" />
            <Tile n={n.visits_today} label="Visits today" tone="accent" to="/today" />
            <Tile n={n.overdue_next_steps} label="Overdue follow-ups" tone="warn" to="/leads" />
          </div>
        </section>
      )}

      {(regs.length > 0 || (money && money.totals.bookings > 0)) && (
        <section className="mb-6 grid gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <div className="rounded-2xl border border-line bg-surface p-4">
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted">Builder registrations to act on · {regs.length}</h2>
            {regs.length === 0 ? <p className="text-sm text-muted">None ending in the next 7 days, and none waiting on a builder.</p> : (
              <ul className="divide-y divide-line text-sm">
                {regs.slice(0, 8).map((a) => (
                  <li key={a.registration_id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                    <Link to={`/leads?lead=${a.lead_id}`} className="min-w-0 truncate text-accent hover:underline">{a.buyer_name ?? `L-${a.ref_no}`} · {a.project_name}</Link>
                    <span className={a.alert === 'not_acknowledged' ? 'text-warn' : 'text-bad'}>
                      {a.alert === 'ended' ? `Ended ${dateOnly(a.valid_until)}` : a.alert === 'ending' ? `Ends ${dateOnly(a.valid_until)} (${a.days_left}d)` : 'Not acknowledged'} · {a.owner_name ?? 'Unassigned'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {money && (
            <Link to="/money" className="rounded-2xl border border-line bg-surface p-4 hover:border-muted">
              <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted">Commission still owed</h2>
              <p className="font-display text-3xl text-accent">{rupees(money.totals.outstanding)}</p>
              <p className="text-sm text-muted">{rupees(money.ageing['61_90'] + money.ageing.over_90)} owed for over 60 days</p>
            </Link>
          )}
        </section>
      )}

      {today && firm && <Digest firmName={firm.name} t={today} />}

      <div className="mb-4 flex flex-wrap gap-1 rounded-lg bg-surface2 p-1 text-sm font-medium sm:w-fit" role="tablist" aria-label="Period">
        {(Object.keys(PERIODS) as PeriodKey[]).map((k) => (
          <button key={k} role="tab" aria-selected={period === k} onClick={() => setPeriod(k)}
            className={`rounded-md px-3 py-1.5 ${period === k ? 'bg-surface shadow' : 'text-muted'}`}>{PERIODS[k]}</button>
        ))}
      </div>

      {data && (
        <>
          <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat n={data.period.new_leads} label="New enquiries" />
            <Stat n={data.period.visits_done} label="Site visits done" />
            <Stat n={data.period.bookings} label="Bookings" />
            <Stat n={data.period.ai_replies} label="AI replies sent" />
          </div>

          <Card title="From enquiry to booking" note="Enquiries in this period, by the furthest stage each one reached.">
            <Funnel rows={data.funnel} />
          </Card>

          <Card title="How fast buyers hear back" note="The Honest Clock: time from the first message to the AI's reply, and to the first call or message from a person.">
            <div className="grid gap-3 sm:grid-cols-3">
              <Stat n={minutes(data.clock.median_ai_minutes)} label="AI first reply (median)" />
              <Stat n={minutes(data.clock.median_human_minutes)} label="First human touch (median)" />
              <Stat n={data.clock.leads_never_touched} label="Enquiries no person has touched" />
            </div>
          </Card>

          <Card title="Where bookings come from" note="By source. Visits and bookings count each enquiry from this period.">
            <Table head={['Source', 'Enquiries', 'Visits done', 'Booked']} rows={data.sources.map((s) => [sourceLabel[s.source] ?? s.source, s.leads, s.visits_done, s.booked])} empty="No enquiries in this period." />
          </Card>

          <Card title="Posts and links" note="Each tracked link's enquiries. WhatsApp Status views are not available to any app, so counting starts when a buyer messages.">
            <Table head={['Link', 'Where', 'Rep', 'Enquiries', 'Visits done', 'Booked']}
              rows={data.links.map((l) => [`${l.code}${l.project ? ` · ${l.project}` : ''}`, [l.channel, l.label].filter(Boolean).join(' · ') || '—', l.rep ?? '—', l.leads, l.visits_done, l.booked])}
              empty={<>No enquiries from tracked links yet. <Link className="text-accent" to="/posts">Share a post</Link>.</>} />
          </Card>

          <Card title="Reps" note="Open leads now; the rest is for this period.">
            <Table head={['Rep', 'Open', 'New', 'First touch (median)', 'Missed time limits', 'Visits done', 'Booked']}
              rows={data.reps.map((r) => [`${r.name}${r.on_leave ? ' (on leave)' : ''} · ${roleLabel[r.role]}`, r.open_leads, r.new_leads, minutes(r.median_first_touch_minutes), r.missed_time_limits, r.visits_done, r.bookings])}
              empty="No reps yet." />
          </Card>

          <Card title="Why leads were lost" note="From the reason chosen when a lead is marked lost.">
            <Table head={['Reason', 'Leads']} rows={data.lost.map((l) => [lostReasonLabel[l.reason] ?? l.reason, l.count])} empty="No leads lost in this period." />
          </Card>
        </>
      )}
    </div>
  );
}

function Tile({ n, label, tone, to }: { n: number; label: string; tone: 'bad' | 'warn' | 'accent'; to: string }) {
  const color = Number(n) > 0 ? { bad: 'text-bad', warn: 'text-warn', accent: 'text-accent' }[tone] : 'text-muted';
  return (
    <Link to={to} className="rounded-2xl border border-line bg-surface p-4 hover:border-muted">
      <p className={`font-display text-3xl tabular-nums ${color}`}>{Number(n)}</p>
      <p className="text-sm text-muted">{label}</p>
    </Link>
  );
}

function Stat({ n, label }: { n: number | string; label: string }) {
  return (
    <div className="rounded-2xl border border-line bg-surface p-4">
      <p className="font-display text-2xl tabular-nums">{typeof n === 'number' ? Number(n) : n}</p>
      <p className="text-sm text-muted">{label}</p>
    </div>
  );
}

function Card({ title, note, children }: { title: string; note?: string; children: ReactNode }) {
  return (
    <section className="mb-5 rounded-2xl border border-line bg-surface p-5">
      <h2 className="font-display text-lg">{title}</h2>
      {note && <p className="mb-3 text-sm text-muted">{note}</p>}
      {children}
    </section>
  );
}

// One series, one hue: bar length is the count against the first stage; the step-to-step rate sits beside it.
function Funnel({ rows }: { rows: ReportSummary['funnel'] }) {
  const top = Math.max(1, Number(rows[0]?.count ?? 0));
  return (
    <ol className="flex flex-col gap-2" aria-label="Funnel from enquiry to booking">
      {rows.map((r, i) => {
        const c = Number(r.count);
        const prev = i > 0 ? Number(rows[i - 1].count) : null;
        const rate = prev ? Math.round((c / prev) * 100) : null;
        return (
          <li key={r.stage} className="grid grid-cols-[7.5rem_minmax(0,1fr)_5.5rem] items-center gap-3 text-sm" title={`${r.stage}: ${c}${rate !== null ? ` (${rate}% of the step before)` : ''}`}>
            <span className="text-muted">{r.stage}</span>
            <span className="h-6 rounded-r bg-surface2">
              <span className="block h-6 rounded-r bg-accent" style={{ width: `${c === 0 ? 0 : Math.max(2, (c / top) * 100)}%` }} />
            </span>
            <span className="tabular-nums"><strong>{c}</strong>{rate !== null && <span className="text-muted"> · {rate}%</span>}</span>
          </li>
        );
      })}
    </ol>
  );
}

function Table({ head, rows, empty }: { head: string[]; rows: Array<Array<string | number>>; empty: ReactNode }) {
  if (!rows.length) return <p className="text-sm text-muted">{empty}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[520px] text-sm">
        <thead><tr className="border-b border-line text-left text-muted">{head.map((h, i) => <th key={h} className={`py-2 pr-3 font-medium ${i ? 'text-right' : ''}`}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-line last:border-0">
              {r.map((c, j) => <td key={j} className={`py-2 pr-3 ${j ? 'text-right tabular-nums' : ''}`}>{typeof c === 'number' || /^\d+$/.test(String(c)) ? Number(c) : c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// The 8 pm summary, ready to send to yourself or your team on WhatsApp.
function Digest({ firmName, t }: { firmName: string; t: ReportSummary }) {
  const [copied, setCopied] = useState(false);
  const text = useMemo(() => {
    const d = new Intl.DateTimeFormat('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short' }).format(new Date());
    return [
      `${firmName} · ${d}`,
      `New enquiries: ${t.period.new_leads} · Visits done: ${t.period.visits_done} · Bookings: ${t.period.bookings}`,
      `AI replies sent: ${t.period.ai_replies} · First human touch (median): ${minutes(t.clock.median_human_minutes)}`,
      `Needs attention: ${t.now.response_late} late responses, ${t.now.needs_you} leads need a person, ${t.now.held_reviews} AI replies to review, ${t.now.unassigned} unassigned`,
      `Visits still booked for today: ${t.now.visits_today}`,
    ].join('\n');
  }, [firmName, t]);
  return (
    <section className="mb-6 rounded-2xl border border-line bg-surface p-5">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-display text-lg">Today's summary</h2>
        <div className="flex gap-2">
          <Button onClick={async () => { await copyText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>{copied ? 'Copied' : 'Copy'}</Button>
          <a className="inline-flex items-center rounded-lg border border-accent bg-accent px-3.5 py-2 text-sm font-medium text-onAccent hover:brightness-110"
            href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noreferrer">Send on WhatsApp</a>
        </div>
      </div>
      <pre className="whitespace-pre-wrap font-body text-sm">{text}</pre>
      <div className="mt-2"><Notice>Sending this automatically at 8 pm needs your WhatsApp number connected and a Meta-approved template. Until then, one tap sends it.</Notice></div>
    </section>
  );
}
